import React from 'react';
import {
    Grid,
    Skeleton,
    styled,
    Table,
    TableBody,
    TableRow,
    TableCell,
    Typography,
} from '@mui/material';

import { formatCurrency } from '../../utils/formattingUtils';
import { ValueReferences } from '../../utils/enums';

const classesPrefix = 'revenue';

const classes = {
    totalRevenueContainer: `${classesPrefix}-totalRevenueContainer`,
    revenuePerOrder: `${classesPrefix}-revenuePerOrder`,
    table: `${classesPrefix}-table`,
    rightTableCol: `${classesPrefix}-rightTableCol`,
    tableContainer: `${classesPrefix}-tableContainer`,
};

const StyledGrid = styled(Grid)(({ theme }) => {
    return {
        [`& .${classes.totalRevenueContainer}`]: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
        },
        [`& .${classes.revenuePerOrder}`]: {
            color: theme.palette.text.secondary,
        },
        [`& .${classes.table}`]: {
            '& td': {
                borderBottom: 'none',
                paddingLeft: 0,
                paddingRight: 0,
            },
        },
        [`& .${classes.rightTableCol}`]: {
            textAlign: 'right',
            fontWeight: 'bold',
        },
        [`& .${classes.tableContainer}`]: {
            display: 'flex',
            alignItems: 'flex-end',
        },
    };
});

const Revenue = ({
    loading,
    totalRevenue,
    linehaulRevenue,
    fuelRevenue,
    accessorialRevenue,
    orderVolume,
}: {
    loading: boolean;
    totalRevenue: number | null;
    linehaulRevenue: number | null;
    fuelRevenue: number | null;
    accessorialRevenue: number | null;
    orderVolume: number | null;
}): React.JSX.Element => {
    const renderTableCell = (revenue: number | null): React.JSX.Element | string => {
        if (loading) {
            return <Skeleton />;
        }

        return `${formatCurrency(revenue)}`;
    };

    const renderRevenuePerOrder = (): React.JSX.Element | string => {
        if (loading) {
            return <Skeleton width={150} />;
        }

        if (totalRevenue === null || !orderVolume) {
            return ValueReferences.NA;
        }

        return `${formatCurrency(totalRevenue / orderVolume)}/order`;
    };

    return (
        <StyledGrid container direction='column' justifyContent='flex-end'>
            <Grid item xs='auto'>
                <Typography variant='h4'>Total Revenue</Typography>
            </Grid>
            <Grid item xs className={classes.totalRevenueContainer}>
                <Typography variant='h3'>
                    {renderTableCell(totalRevenue)}
                </Typography>
                <Typography variant='h5' className={classes.revenuePerOrder}>
                    {renderRevenuePerOrder()}
                </Typography>
            </Grid>
            <Grid item xs='auto' className={classes.tableContainer}>
                <Table className={classes.table} size='small'>
                    <TableBody>
                        <TableRow>
                            <TableCell>Line Haul Revenue</TableCell>
                            <TableCell className={classes.rightTableCol}>
                                {renderTableCell(linehaulRevenue)}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Fuel Revenue</TableCell>
                            <TableCell className={classes.rightTableCol}>
                                {renderTableCell(fuelRevenue)}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Accessorial Charges</TableCell>
                            <TableCell className={classes.rightTableCol}>
                                {renderTableCell(accessorialRevenue)}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </Grid>
        </StyledGrid>
    );
};

export default Revenue;
